import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils/cn';

/**
 * Variants : un seul accent par écran — preferer `primary` pour CTA principal,
 * `terracotta` pour issue Live key (rare), `ghost` ou `outline` partout ailleurs.
 */
const buttonVariants = cva(
    [
        'inline-flex items-center justify-center gap-2',
        'rounded-md font-medium transition-colors duration-fast ease-out-expo',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:pointer-events-none disabled:opacity-50',
    ].join(' '),
    {
        variants: {
            variant: {
                primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
                destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                outline: 'border border-border bg-background text-foreground hover:bg-muted',
                outlineOnDark: 'border border-white/20 bg-white/10 text-white hover:bg-white/20',
                ghost: 'text-foreground hover:bg-muted',
                link: 'text-foreground underline-offset-4 hover:underline',
            },
            size: {
                sm: 'h-8 px-3 text-xs',
                md: 'h-9 px-4 text-sm',
                lg: 'h-10 px-5 text-sm',
                icon: 'h-9 w-9',
                'icon-sm': 'h-8 w-8',
            },
        },
        defaultVariants: {
            variant: 'outline',
            size: 'md',
        },
    },
);

export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    { className, variant, size, asChild = false, type, ...props },
    ref,
) {
    const Comp = asChild ? Slot : 'button';
    return (
        <Comp
            ref={ref}
            type={asChild ? undefined : (type ?? 'button')}
            className={cn(buttonVariants({ variant, size }), className)}
            {...props}
        />
    );
});
